import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Pre-join form data captured before room connection.
 * This is persisted to Redux BEFORE any navigation to prevent data loss.
 */
interface PreJoinData {
  caseNumber: string | null;
  badgeNumber: string | null;
  language: string | null;
  officerName: string | null;
  /**
   * The officer's full name from their profile ("Andrew Kelly"). `officerName`
   * is the in-call display name ("Officer Kelly"), which a caller cannot use to
   * reach anyone, so caller-facing contact details use this one.
   */
  officerFullName?: string | null;
  expectedCallLengthMinutes: number | null;
}

export interface CallInfoState {
  /**
   * The canonical `calls.id` UUID for the LIVE call, from connection-details
   * at join (sol r59 finding 4: the translation scope carries the ROOM NAME
   * live, and no live component threaded the uuid, so every uuid-keyed API —
   * the transcript projection first among them — was unreachable from a live
   * tile). Null until the join resolves it, and for callers whose
   * connection-details predate the field.
   *
   * Room-scoped (sol r62 finding 2): `callUuidRoom` names the room the uuid
   * belongs to, so a LATER uuid-less response for the SAME room cannot clear
   * better information (an officer reload races the cached-session restore
   * against auto-resume's own connection-details fetch — "no calls row" from
   * the second must not erase the uuid the first restored), while a response
   * for a DIFFERENT room still clears (call A's uuid must never feed call B).
   */
  callUuid: string | null;
  callUuidRoom: string | null;
  incidentNumber: string | null;
  phonePin: string | null;
  /** True once the dial-in provisioning request has resolved (with OR without a
   *  PIN) — lets the UI show a terminal state instead of "Setting up..." forever
   *  when SIP dial-in yields no PIN. */
  phonePinResolved: boolean;
  phoneNumber: string | null;
  /** Pre-join form data - written before navigation to survive page transitions */
  preJoinData: PreJoinData | null;
  /** Participant avatars captured from video - keyed by participant identity */
  participantAvatars: Record<string, string>;
  /** Local officer's profile avatar URL — read by self-view PiP fallback. Updated optimistically on upload. */
  selfAvatarUrl: string | null;
  /** Set when backend signals enough data gathered — call ready to archive */
  callComplete: boolean;
}

const initialState: CallInfoState = {
  callUuid: null,
  callUuidRoom: null,
  incidentNumber: null,
  phonePin: null,
  phonePinResolved: false,
  phoneNumber: null,
  preJoinData: null,
  participantAvatars: {},
  selfAvatarUrl: null,
  callComplete: false,
};

const callInfoSlice = createSlice({
  name: 'callInfo',
  initialState,
  reducers: {
    setIncidentNumber: (state, action: PayloadAction<string>) => {
      state.incidentNumber = action.payload;
    },
    /**
     * The live call's canonical UUID, from connection-details at join or the
     * cached-session restore. A NULL for the room we already hold a uuid for
     * is weaker information and keeps the stored value; anything else —
     * a real uuid, or a null for a different room — takes effect.
     */
    setCallUuid: (
      state,
      action: PayloadAction<{ roomName: string; callUuid: string | null }>,
    ) => {
      const { roomName, callUuid } = action.payload;
      if (callUuid === null && state.callUuid !== null && state.callUuidRoom === roomName) {
        return;
      }
      state.callUuid = callUuid;
      state.callUuidRoom = callUuid === null ? null : roomName;
    },
    setPhonePin: (state, action: PayloadAction<string>) => {
      state.phonePin = action.payload;
      state.phonePinResolved = true;
    },
    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload;
    },
    setCallComplete: (state, action: PayloadAction<boolean>) => {
      state.callComplete = action.payload;
    },
    clearCallInfo: (state) => {
      state.callUuid = null;
      state.callUuidRoom = null;
      state.incidentNumber = null;
      state.phonePin = null;
      state.phoneNumber = null;
      state.preJoinData = null;
      state.callComplete = false;
    },
    /** Set pre-join form data - called BEFORE navigation to persist form values */
    setPreJoinData: (state, action: PayloadAction<PreJoinData>) => {
      state.preJoinData = action.payload;
      // Also sync incidentNumber for backwards compatibility
      if (action.payload.caseNumber) {
        state.incidentNumber = action.payload.caseNumber;
      }
    },
    /** Clear only pre-join data (keep other call info) */
    clearPreJoinData: (state) => {
      state.preJoinData = null;
    },
    /** Set a participant's avatar image (base64 data URL) */
    setParticipantAvatar: (state, action: PayloadAction<{ identity: string; avatar: string }>) => {
      state.participantAvatars[action.payload.identity] = action.payload.avatar;
    },
    /** Clear all participant avatars */
    clearParticipantAvatars: (state) => {
      state.participantAvatars = {};
    },
    /** Set the local officer's profile avatar URL (used by self-view PiP fallback) */
    setSelfAvatarUrl: (state, action: PayloadAction<string | null>) => {
      state.selfAvatarUrl = action.payload;
    },
    /** Hydrate entire state (for demo mode or state restoration) */
    hydrateCallInfoState: (state, action: PayloadAction<Partial<CallInfoState>>) => {
      if (action.payload.incidentNumber !== undefined) {
        state.incidentNumber = action.payload.incidentNumber;
      }
      if (action.payload.phonePin !== undefined) {
        state.phonePin = action.payload.phonePin;
      }
      if (action.payload.phonePinResolved !== undefined) {
        state.phonePinResolved = action.payload.phonePinResolved;
      }
      if (action.payload.phoneNumber !== undefined) {
        state.phoneNumber = action.payload.phoneNumber;
      }
      if (action.payload.preJoinData !== undefined) {
        state.preJoinData = action.payload.preJoinData;
      }
      if (action.payload.participantAvatars !== undefined) {
        state.participantAvatars = action.payload.participantAvatars;
      }
      if (action.payload.selfAvatarUrl !== undefined) {
        state.selfAvatarUrl = action.payload.selfAvatarUrl;
      }
    },
  },
});

/**
 * The ONE extraction rule for a live call's UUID from connection details —
 * used by BOTH the normal-join dispatch and the cached-session restore
 * (sol r60 finding 2: the restore path bypassed the join-site dispatch, so a
 * reload left `callUuid` null and every uuid-keyed live surface dark). A
 * shared helper makes the two sites structurally identical and testable.
 */
export function callUuidFromConnectionDetails(
  details: { callUuid?: string | null } | null | undefined,
): string | null {
  if (!details) return null;
  return typeof details.callUuid === 'string' && details.callUuid ? details.callUuid : null;
}

export const { setIncidentNumber, setCallUuid, setPhonePin, setPhoneNumber, setCallComplete, clearCallInfo, setPreJoinData, clearPreJoinData, setParticipantAvatar, clearParticipantAvatars, setSelfAvatarUrl, hydrateCallInfoState } = callInfoSlice.actions;
export default callInfoSlice.reducer;
